import { useState, useEffect, useMemo } from "react";
import ModalBasicLayout from "../ModalBasicLayout";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import { Coins, Video } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { startCreatePurchaseCreditsPaymentLink } from "../../../actions/payments";
import { setShowModalSelectSubscription } from "../../../actions/ui";
import {
  MODAL_PURCHASE_CREDITS_TITLE,
  MODAL_PURCHASE_CREDITS_ARIA_LABEL,
  MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_TITLE,
  MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_1,
  MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_2,
  MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_3,
  MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_3_LINK,
  MODAL_PURCHASE_CREDITS_UPGRADE_PLAN,
  MODAL_PURCHASE_CREDITS_PURCHASE_BUTTON,
  MODAL_PURCHASE_CREDITS_PURCHASE_VIDEOS_BUTTON,
  MODAL_PURCHASE_CREDITS_CREDITS_LABEL,
  MODAL_PURCHASE_CREDITS_PRICE_PER_CREDIT,
  MODAL_PURCHASE_CREDITS_CREDIT_PACKAGES_TITLE,
  MODAL_PURCHASE_CREDITS_VIDEO_PACKAGES_TITLE,
  MODAL_PURCHASE_CREDITS_VIDEO_PACKAGES_SUBTITLE,
  MODAL_PURCHASE_CREDITS_VIDEO_ON_SCREEN_TEXT_LANGUAGES,
  MODAL_PURCHASE_CREDITS_VIDEOS_LABEL,
  MODAL_PURCHASE_CREDITS_PRICE_PER_VIDEO,
} from "../../../i18n/keysTranslations";
import {
  BOX_SHADOW_THEME,
  GRAY_100,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import {
  BORDER_RADIUS,
  MODAL_CREATE_WITH_IA_TEXT_LANGUAGE_CODES_VIDEO,
} from "../../../utils/constants";
import { optionsLanguage } from "../../../utils/options";
import {
  DEFAULT_CREDIT_PURCHASE_PACKAGES,
  DEFAULT_VIDEO_PURCHASE_PACKAGES,
} from "../../../utils/creditActionCosts";
import VideoPurchaseExamples from "./VideoPurchaseExamples";

const ModalPurchaseCredits = ({
  modalOpen,
  onCloseModal,
  onPurchase = () => {},
  onUpgradePlan = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const creditPackages =
    useSelectorApp((state) => state.payments?.creditPurchasePackages) ??
    DEFAULT_CREDIT_PURCHASE_PACKAGES;
  const videoPackagesFromStore = useSelectorApp(
    (state) => state.payments?.videoPurchasePackages
  );
  const videoPackages =
    videoPackagesFromStore ?? DEFAULT_VIDEO_PURCHASE_PACKAGES;

  const videoOnScreenTextLanguageOptions = useMemo(
    () =>
      optionsLanguage.filter((l) =>
        MODAL_CREATE_WITH_IA_TEXT_LANGUAGE_CODES_VIDEO.includes(l.value)
      ),
    []
  );

  const [selection, setSelection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onSelectCredits = (credits) => {
    setSelection({ type: "credits", credits });
  };

  const onSelectVideos = (videos) => {
    setSelection({ type: "video", videos });
  };

  const onPurchaseClick = async () => {
    if (!selection) return;
    setIsLoading(true);
    const payload =
      selection.type === "credits"
        ? { quantity: selection.credits, purchaseKind: "credits" }
        : { quantity: selection.videos, purchaseKind: "videos" };
    const response = await dispatch(
      startCreatePurchaseCreditsPaymentLink(payload)
    );
    setIsLoading(false);
    if (response.ok && response.data?.url) {
      window.location.href = response.data.url;
      onPurchase(
        selection.type === "credits" ? selection.credits : selection.videos
      );
    }
  };

  const canPurchase = Boolean(selection);

  const openUpgradePaywall = () => {
    onUpgradePlan();
    dispatch(setShowModalSelectSubscription(true));
    onCloseModal();
  };

  useEffect(() => {
    if (!modalOpen) {
      setSelection(null);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (selection == null) return;
    if (selection.type === "credits") {
      const valid = creditPackages.some((p) => p.credits === selection.credits);
      if (!valid) setSelection(null);
      return;
    }
    const valid = videoPackages.some((p) => p.videos === selection.videos);
    if (!valid) setSelection(null);
  }, [creditPackages, videoPackages, selection]);

  const howItWorksItems = [
    MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_1,
    MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_2,
    MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_3,
  ];

  const purchaseButtonKey =
    selection?.type === "video"
      ? MODAL_PURCHASE_CREDITS_PURCHASE_VIDEOS_BUTTON
      : MODAL_PURCHASE_CREDITS_PURCHASE_BUTTON;

  const rowSx = (theme, isSelected) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 2,
    px: 2,
    py: 1,
    borderRadius: BORDER_RADIUS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: `1px solid ${INPUT_COLOR_BORDER}`,
    borderColor: isSelected ? theme.palette.primary.main : INPUT_COLOR_BORDER,
    backgroundColor: isSelected
      ? theme.palette.action.selected
      : theme.palette.background.paper,
    boxShadow: BOX_SHADOW_THEME,
    "&:hover": {
      backgroundColor: isSelected
        ? theme.palette.action.selected
        : theme.palette.action.hover,
    },
  });

  const iconWrapSx = (theme, isSelected) => ({
    width: 40,
    height: 40,
    borderRadius: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isSelected ? theme.palette.text.primary : GRAY_100,
    color: isSelected
      ? theme.palette.background.paper
      : theme.palette.text.primary,
    flexShrink: 0,
  });

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_PURCHASE_CREDITS_TITLE)}
      maxWidth="sm"
      fullWidth
      ariaLabel={t(MODAL_PURCHASE_CREDITS_ARIA_LABEL)}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {/* How purchasing credits works */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "0.875rem",
            }}
          >
            {t(MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_TITLE)}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
            {howItWorksItems.map((key, index) => (
              <Box
                key={index}
                component="li"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                    flexShrink: 0,
                  }}
                >
                  •
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.secondary",
                    lineHeight: 1.5,
                  }}
                >
                  {t(key)}
                  {key === MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_3 && (
                    <>
                      {" "}
                      <Box
                        component="button"
                        type="button"
                        onClick={openUpgradePaywall}
                        sx={{
                          display: "inline",
                          p: 0,
                          m: 0,
                          border: 0,
                          background: "none",
                          cursor: "pointer",
                          font: "inherit",
                          fontSize: "inherit",
                          lineHeight: "inherit",
                          fontWeight: 700,
                          color: "text.primary",
                          textDecoration: "underline",
                          textUnderlineOffset: "2px",
                          "&:hover": {
                            opacity: 0.75,
                          },
                        }}
                      >
                        {t(MODAL_PURCHASE_CREDITS_HOW_IT_WORKS_ITEM_3_LINK)}
                      </Box>
                      .
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Packages */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            py: 1,
          }}
        >
          {videoPackages.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  {t(MODAL_PURCHASE_CREDITS_VIDEO_PACKAGES_TITLE)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    lineHeight: 1.4,
                    mt: 0.25,
                  }}
                >
                  {t(MODAL_PURCHASE_CREDITS_VIDEO_PACKAGES_SUBTITLE)}
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    lineHeight: 1.45,
                    mt: 1,
                    mb: 0.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {t(
                      MODAL_PURCHASE_CREDITS_VIDEO_ON_SCREEN_TEXT_LANGUAGES
                    )}{" "}
                  </Box>
                  {videoOnScreenTextLanguageOptions
                    .map((l) => t(l.label))
                    .join(", ")}
                  .
                </Typography>
              </Box>
              <VideoPurchaseExamples />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {videoPackages.map((pkg) => {
                  const isSelected =
                    selection?.type === "video" &&
                    selection.videos === pkg.videos;

                  return (
                    <Box
                      key={`video-${pkg.videos}`}
                      onClick={() => onSelectVideos(pkg.videos)}
                      sx={(theme) => rowSx(theme, isSelected)}
                    >
                      <Box sx={(theme) => iconWrapSx(theme, isSelected)}>
                        <Video size={20} />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            color: "text.primary",
                          }}
                        >
                          {t(MODAL_PURCHASE_CREDITS_VIDEOS_LABEL, {
                            videos: pkg.videos.toLocaleString(),
                          })}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                          }}
                        >
                          {t(MODAL_PURCHASE_CREDITS_PRICE_PER_VIDEO, {
                            price: pkg.pricePerUnit.toFixed(2),
                          })}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          color: "text.primary",
                        }}
                      >
                        ${pkg.price.toLocaleString()}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: "0.875rem",
                color: "text.primary",
              }}
            >
              {t(MODAL_PURCHASE_CREDITS_CREDIT_PACKAGES_TITLE)}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {creditPackages.map((pkg) => {
                const isSelected =
                  selection?.type === "credits" &&
                  selection.credits === pkg.credits;

                return (
                  <Box
                    key={pkg.credits}
                    onClick={() => onSelectCredits(pkg.credits)}
                    sx={(theme) => rowSx(theme, isSelected)}
                  >
                    <Box sx={(theme) => iconWrapSx(theme, isSelected)}>
                      <Coins size={20} />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          color: "text.primary",
                        }}
                      >
                        {t(MODAL_PURCHASE_CREDITS_CREDITS_LABEL, {
                          credits: pkg.credits.toLocaleString(),
                        })}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "text.secondary",
                        }}
                      >
                        {t(MODAL_PURCHASE_CREDITS_PRICE_PER_CREDIT, {
                          price: pkg.pricePerCredit.toFixed(2),
                        })}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "text.primary",
                      }}
                    >
                      ${pkg.price.toLocaleString()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* DialogActions stays outside the scrollable content so the buttons stay visible */}
      <DialogActions
        disableSpacing
        sx={(theme) => ({
          px: { xs: 3, sm: 5 },
          pt: 3,
          pb: 3,
          borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <ContainerModalActions
          hasPadding={false}
          sx={{
            flexDirection: { xs: "column-reverse", sm: "row" },
          }}
          propsSecondaryButton={{
            showButton: true,
            text: MODAL_PURCHASE_CREDITS_UPGRADE_PLAN,
            onClick: openUpgradePaywall,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: purchaseButtonKey,
            onClick: onPurchaseClick,
            disabled: !canPurchase,
            loading: isLoading,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalPurchaseCredits;
